import { IElement, OneOfPropertyElements } from '../models/project.model';
import * as uuid from 'uuid';
import { omitUndefined } from './object.helpers';
import {
  createExpression,
  isExpressionValue,
} from './expression-factory.helpers';
import { isNumeric } from './math.helpers';
import { guards } from './typeguards.helpers';
import {
  CreatedElementProperty,
  ElementPropertyType,
  ElementPropertyTypeMap,
  ElementPropertySource,
  ElementPropertySourceID,
  FactoryPropertyInputTypeMap,
  IElementExpressionProperty,
  IElementProperty,
  IElementPropertyFactoryOption,
  IElementPropertyOption,
  IElementPropertySourceIdProperties,
  IElementSelectProperty,
  IElementSwitchProperty,
  IFactoryExpressionProperty,
  IFactoryProperty,
  IFactorySelectProperty,
  IFactorySwitchProperty,
} from '../models/element-property.model';
import { createId, RegenerateIds, shouldRegenerateIds } from './id.helpers';
import {
  getElementProperties,
  getElementPropertyType,
  getSelectPropertyResolvedCount,
  getSelectPropertyOptionValue,
  hasCount,
  isSelectPropertyOptionValue,
} from './element-property.helpers';
import { findFreeName, labelFromValue } from './string.helpers';
import { validateElementProperty } from '../validation/element-property.validation';
import { resolveAutoRecipeId } from './recipe.helpers';
import { isDefined, isEmptyArray } from './array.helpers';
import { OptionalReadonly } from '../models/type-helpers.model';
import { formatExpressionVariableName } from './expression-variables.helpers';
import {
  contentCoverageFactoryOverridesFrom,
  isContentCoverageFactoryShape,
  toContentCoverageFactory,
} from '../templates/categories/common-element-category-properties.helpers';

/**
 * Create a new property of a specific type.
 * Can also be used to shallow clone a property.
 * @param defaults A partial with overides of the default values. Need to contain kind
 * @returns
 */
export function addElementProperties<
  T extends IFactoryProperty,
  E extends OneOfPropertyElements,
>(element: E, ...propertyDefaults: T[]): E {
  // Make sure to clone properties
  const properties = [...getElementProperties(element)];

  if (!propertyDefaults?.length) {
    return element;
  }
  propertyDefaults.forEach((defaults) =>
    properties.push(createElementProperty(defaults, properties)),
  );
  return {
    ...element,
    properties,
  };
}

/**
 * Options for {@link createElementProperty} and friends.
 *
 * `skipValidation` lets enrich-pipeline helpers ({@link getElementPropertiesFromCategory},
 * {@link filterConditionalProperties}) bypass {@link validateElementProperty}, which fails
 * for select properties whose stored `count` is no longer in the freshly-resolved options
 * list (e.g. switching `material` from Aluminium → Steel keeps `form: Profile` from the
 * previous category). Validation belongs at the user-input boundary, not inside transforms
 * that re-derive properties from the category template.
 */
export type CreateElementPropertyOptions = {
  skipValidation?: boolean;
};

/**
 * Create a new property of a specific type.
 * Can also be used to shallow clone a property.
 * @param defaults A partial with overides of the default values. Need to contain kind
 * @param existingProperties Used to generate a unique name when defaults.name is taken
 * @param regenerateIds When true, replace any incoming id with a fresh uuid
 * @param options See {@link CreateElementPropertyOptions}
 * @returns
 */
export function createElementProperty<
  T extends IFactoryProperty = IFactoryExpressionProperty,
>(
  defaults: T = {} as T,
  existingProperties: IElementProperty[] = [],
  regenerateIds?: boolean,
  options: CreateElementPropertyOptions = {},
): CreatedElementProperty<T['type']> {
  if (isContentCoverageFactoryShape(defaults)) {
    return createElementPropertyOfInputType(
      ElementPropertyType.Expression,
      omitContentCoverageCountOutsideBounds(
        toContentCoverageFactory(contentCoverageFactoryOverridesFrom(defaults)),
      ),
      existingProperties,
      regenerateIds,
      options,
    ) as CreatedElementProperty<T['type']>;
  }

  return createElementPropertyOfInputType(
    defaults.type,
    defaults,
    existingProperties,
    regenerateIds,
    options,
  ) as CreatedElementProperty<T['type']>;
}

/**
 * Create an array of element properties from a list of factory properties.
 * Also makes sure names are unique.
 * @param factoryProperties
 * @param regenerateIds
 * @returns
 */
export const createElementProperties = (
  factoryProperties: IFactoryProperty[] = [],
  regenerateIds?: RegenerateIds,
): IElementProperty[] => {
  const regenerateBool = shouldRegenerateIds(regenerateIds);
  return factoryProperties.reduce(
    (properties, factoryProperty) => [
      ...properties,
      createElementProperty(
        withoutSelectCountsOutsideOptions(factoryProperty),
        properties,
        regenerateBool,
      ),
    ],
    [] as IElementProperty[],
  );
};

/**
 * {@link createElementProperties} validates every rebuilt property. Drop a
 * select count the current options no longer contain so that rebuild can use
 * the definition default instead of throwing. Direct
 * {@link createElementProperty} calls, including user edits, still validate.
 *
 * @param factoryProperty - One factory row from a rebuild
 */
const withoutSelectCountsOutsideOptions = (
  factoryProperty: IFactoryProperty,
): IFactoryProperty => {
  if (factoryProperty.type !== ElementPropertyType.Select) {
    return factoryProperty;
  }
  return omitSelectCountsOutsideOptions(factoryProperty);
};

/**
 * Create a new ElementProperty with recipe_id or category_id set
 * @param element
 * @param source
 * @param sourceId
 * @returns
 */
export const createElementPropertyOfSource = <
  T extends IFactoryProperty = IFactoryExpressionProperty,
>(
  element: IElement,
  source?: ElementPropertySource,
  sourceId?: ElementPropertySourceID,
  defaults: T = {} as T,
  regenerateIds?: boolean,
): CreatedElementProperty<T['type']> => {
  return createElementProperty(
    {
      defaults,
      ...createSourceIdProperty(source, sourceId),
    },
    element.properties,
    regenerateIds,
  ) as CreatedElementProperty<T['type']>;
};

/**
 * Create a new property of a specific type.
 * Can also be used to shallow clone a property.
 * @param type Type of property which should be created, default to expression
 * @param defaults A partial with overides of the default values
 * @param existingProperties Optional existingProperties used to generate unique name for property
 * @returns
 */
export function createElementPropertyOfInputType<
  T extends keyof ElementPropertyTypeMap = ElementPropertyType.Expression,
>(
  type: T = ElementPropertyType.Expression as T,
  defaults: Partial<FactoryPropertyInputTypeMap[T]> = {},
  existingProperties?: IElementProperty[],
  regenerateIds?: boolean,
  options: CreateElementPropertyOptions = {},
): CreatedElementProperty<T> {
  switch (type) {
    case ElementPropertyType.Expression: {
      return createElementExpressionProperty(
        defaults as IFactoryExpressionProperty,
        existingProperties,
        regenerateIds,
        options,
      ) as CreatedElementProperty<T>;
    }
    case ElementPropertyType.Select: {
      return createElementSelectProperty(
        defaults as IFactorySelectProperty,
        existingProperties,
        regenerateIds,
        options,
      ) as CreatedElementProperty<T>;
    }
    case ElementPropertyType.Switch: {
      return createElementSwitchProperty(
        defaults as IFactorySwitchProperty,
        existingProperties,
        regenerateIds,
        options,
      ) as CreatedElementProperty<T>;
    }

    default: {
      throw new Error(`Can't create element of unknown type ${type as string}`);
    }
  }
}

/**
 * Resolved number for a literal count. Formulas are omitted so a placeholder
 * `resolved: 0` is not treated as a value below min.
 */
const numericResolvedCount = (
  count: IFactoryExpressionProperty['count'],
): number | undefined => {
  if (guards.is.finiteNumber(count)) return count;
  if (guards.is.string(count) && isNumeric(count)) return +count;
  if (isExpressionValue(count) && isNumeric(count.expression)) {
    return count.resolved;
  }
  return undefined;
};

/**
 * Drop a content-coverage count that is outside min/max.
 * Enrich stores `0` with validation skipped; applying that count throws
 * because content coverage min is 0.01. Fallback count then applies instead.
 *
 * @param factory - Content coverage factory, including injected min/max
 */
const omitContentCoverageCountOutsideBounds = (
  factory: IFactoryExpressionProperty,
): IFactoryExpressionProperty => {
  const resolved = numericResolvedCount(factory.count);
  if (resolved === undefined) return factory;
  const belowMin = guards.is.number(factory.min) && resolved < factory.min;
  const aboveMax = guards.is.number(factory.max) && resolved > factory.max;
  if (!belowMin && !aboveMax) return factory;
  const { count: _count, ...withoutCount } = factory;
  return withoutCount;
};

/**
 * True when `count` would fail {@link validateElementProperty} because it is
 * not in the resolved options. An empty options list is not outside: that
 * count becomes the only option.
 *
 * @param defaults - Select factory whose options resolve the allowed values
 * @param count - Candidate count or fallback count
 */
const selectCountIsOutsideResolvedOptions = (
  defaults: IFactorySelectProperty,
  count: unknown,
): boolean => {
  // Same truthiness rule as select validation: 0 and '' are not rejected.
  if (!count) return false;
  const options = defaults.options;
  if (!options || isEmptyArray(options)) return false;
  return !isSelectPropertyOptionValue(
    { ...defaults, type: ElementPropertyType.Select },
    count,
  );
};

/**
 * Drop select `count` and `fallbackCount` values that are not in the resolved
 * options. A carried-over count (for example `form: Profile` after the
 * material options change) would otherwise fail validation. The definition
 * default then applies: the first option, or a remaining fallback.
 *
 * @param defaults - Select factory input, including count and options
 */
const omitSelectCountsOutsideOptions = (
  defaults: IFactorySelectProperty,
): IFactorySelectProperty => {
  let next = defaults;
  const countOutside = selectCountIsOutsideResolvedOptions(next, next.count);
  if (countOutside) {
    const { count: _omittedCount, ...withoutCount } = next;
    next = withoutCount;
  }
  if (selectCountIsOutsideResolvedOptions(next, next.fallbackCount)) {
    const { fallbackCount: _omittedFallback, ...withoutFallback } = next;
    next = withoutFallback;
  }
  if (!countOutside || hasCount(next) || next.inheritFallback) return next;
  if (next.fallbackCount === undefined) return next;
  // Select validation still requires a count unless inheritFallback is set.
  // A remaining fallback is the same value resolution would have chosen.
  const resolved = getSelectPropertyResolvedCount(next);
  if (resolved === undefined) return next;
  return { ...next, count: resolved };
};

/** See {@link CreateElementPropertyOptions.skipValidation}. */
const validatePropertyOrPassthrough = <T extends IElementProperty>(
  property: T,
  options: CreateElementPropertyOptions,
): T => (options.skipValidation ? property : validateElementProperty(property));

/**
 * Create a new ElementProperty
 * @param defaults Default values for the property
 * @param element Optional element generate unique name for property
 * @param existingProperties Optional existingProperties used to generate unique name for property
 * @returns
 */
const createElementExpressionProperty = (
  defaults: IFactoryExpressionProperty = {},
  existingProperties?: IElementProperty[],
  regenerateIds = true,
  options: CreateElementPropertyOptions = {},
): IElementExpressionProperty => {
  // If passing options we're trying to create a select property (happens a lot in unit test so keep this check)
  if ('options' in defaults) {
    throw new Error('Options are not allowed on expression properties');
  }

  const defaultCount = getCountFromDefaults(defaults, 0);
  const count =
    defaultCount !== undefined ? createExpression(defaultCount) : undefined;
  const fallbackCount =
    defaults.fallbackCount !== undefined || defaults.inheritFallback
      ? createExpression(defaults.fallbackCount)
      : undefined;

  return validatePropertyOrPassthrough(
    {
      unit: 'kg',
      ...omitUndefined({ ...defaults, count, fallbackCount }),
      id: createId(defaults, regenerateIds),
      name: getPropertyName(defaults, existingProperties),
      type: ElementPropertyType.Expression,
    },
    options,
  );
};

/**
 * Create a new ElementProperty
 * @param defaults Default values for the property
 * @param existingProperties Optional existingProperties used to generate unique name for property
 * @returns
 */
const createElementSelectProperty = (
  defaults: IFactorySelectProperty = { type: ElementPropertyType.Select },
  existingProperties?: IElementProperty[],
  regenerateIds = true,
  options: CreateElementPropertyOptions = {},
): IElementSelectProperty => {
  // Legacy rows may still carry Select + count; always normalize content coverage.
  if (isContentCoverageFactoryShape(defaults)) {
    return createElementProperty(
      defaults,
      existingProperties,
      regenerateIds,
      options,
    ) as IElementSelectProperty;
  }

  // If value can be inherited, don't set a count. Else get count from defaults or options
  const count = getCountFromDefaults(
    defaults,
    getSelectPropertyResolvedCount(defaults),
  );
  const defaultOptions = defaults.options ?? [];

  // Fallback to count if no options are provided to at least provide one value
  const propertyOptions = Array.isArray(defaultOptions)
    ? createPropertySelectOptions(
        isEmptyArray(defaultOptions) ? [count] : defaultOptions,
      )
    : defaultOptions;

  return validatePropertyOrPassthrough(
    {
      ...omitUndefined({ ...defaults, count }),
      type: ElementPropertyType.Select,
      name: getPropertyName(defaults, existingProperties),
      id: createId(defaults, regenerateIds),
      options: propertyOptions,
    },
    options,
  );
};

/**
 * Create a new ElementProperty
 * @param defaults Default values for the property
 * @param existingProperties Optional existingProperties used to generate unique name for property
 * @returns
 */
const createElementSwitchProperty = (
  defaults: Partial<IFactorySwitchProperty> = {},
  existingProperties?: IElementProperty[],
  regenerateIds = true,
  options: CreateElementPropertyOptions = {},
): IElementSwitchProperty => {
  const count = toSwitchCount(getCountFromDefaults(defaults, false));

  return validatePropertyOrPassthrough(
    {
      ...omitUndefined({ ...defaults, count }),
      type: ElementPropertyType.Switch,
      name: getPropertyName(defaults, existingProperties),
      id: createId(defaults, regenerateIds),
    },
    options,
  );
};

export const toSwitchCount = (
  count?: boolean | 'true' | 'false',
): boolean | undefined => {
  if (typeof count === 'boolean') {
    return count;
  }
  if (count === 'true' || count === 'false') {
    return count === 'true';
  }
  return undefined;
};

/**
 * Get the count from the defaults
 * @param defaults
 * @returns
 */
const getCountFromDefaults = <T extends Partial<IFactoryProperty>>(
  defaults: T,
  defaultValue: T['count'],
): T['count'] | undefined => {
  if (hasCount(defaults)) {
    return defaults.count;
  }
  // If inheritFallback or fallbackCount is provided, we can have undefined count
  if (defaults.inheritFallback || defaults.fallbackCount !== undefined) {
    return undefined;
  }
  return defaultValue;
};

/**
 * Create a new ElementProperty
 * @param defaults Default values for the property
 * @param element Optional element generate unique name for property
 * @returns
 */
export const getPropertyName = (
  { name = 'property' }: Pick<IFactoryProperty, 'name'> = {},
  existingProperties: IElementProperty[] = [],
): string => {
  name = formatExpressionVariableName(name);
  const names = existingProperties?.map((property) => property.name);
  return findFreeName(names, name, {
    delimiter: '',
    startIndex: 1,
  });
};

/**
 * Create a new ElementProperty with recipe_id or category_id set
 * @param element
 * @param source
 * @param sourceId
 * @returns
 */
export const createSourceIdProperty = (
  source?: ElementPropertySource,
  sourceId?: ElementPropertySourceID,
): IElementPropertySourceIdProperties =>
  source && sourceId !== undefined
    ? {
        [source]:
          source === ElementPropertySource.Recipe
            ? resolveAutoRecipeId(sourceId)
            : sourceId,
      }
    : {};

/**
 * Create a valid options array from list of "stripped" options
 * @param options
 * @returns
 */
export const createPropertySelectOptions = (
  options: OptionalReadonly<(IElementPropertyFactoryOption | undefined)[]>,
): IElementPropertyOption[] =>
  Object.values(options) // Why is TS complaining if not wrapping in values?
    .filter(isDefined)
    .map((option) => {
      const value = getSelectPropertyOptionValue(option);
      const label =
        (typeof option === 'object' && option.label) || labelFromValue(value);
      return { label, value };
    });

/**
 * Apply a new id and [type]: typeId to property
 * @param property
 * @param source
 * @param sourceId
 * @returns
 */
export const applyIdAndSourceToProperty = <T extends IElementProperty>(
  property: Omit<T, 'id'>,
  source?: ElementPropertySource,
  sourceId?: ElementPropertySourceID,
): T => {
  return {
    ...property,
    id: uuid.v4(),
    ...createSourceIdProperty(source, sourceId),
  } as T;
};

export const isFactoryExpressionProperty = (
  prop: IFactoryProperty | undefined,
): prop is IFactoryExpressionProperty =>
  !!prop && getElementPropertyType(prop) === ElementPropertyType.Expression;
